import { trackEvent } from "./etracker";

const FORM_SELECTOR = "form.marketingForm";
const DYNAMICS_HOST = "mkt.dynamics.com";

let loadedTracked = false;
let successTracked = false;

function fireLoaded(): void {
  if (loadedTracked) return;
  loadedTracked = true;
  trackEvent({
    object: "Registration Form",
    category: "Form",
    action: "Loaded",
    type: "form_loaded",
  });
}

function fireError(): void {
  trackEvent({
    object: "Registration Form",
    category: "Form",
    action: "Validation Error",
    type: "form_error",
  });
}

function fireSuccess(): void {
  if (successTracked) return;
  successTracked = true;
  trackEvent({
    object: "Registration Form",
    category: "Form",
    action: "Submit Success",
    type: "form_success",
  });
}

function patchFetch(): void {
  if (typeof window.fetch !== "function") return;

  const _fetch = window.fetch.bind(window);

  window.fetch = async function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url = input instanceof Request ? input.url : String(input);
    const method = (init?.method ?? "GET").toUpperCase();

    const response = await _fetch(input, init);

    if (url.includes(DYNAMICS_HOST) && method === "POST" && response.ok) {
      fireSuccess();
    }

    return response;
  };
}

function patchXHR(): void {
  const NativeXHR = window.XMLHttpRequest;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).XMLHttpRequest = class extends NativeXHR {
    private _url = "";
    private _method = "GET";

    open(
      method: string,
      url: string | URL,
      async = true,
      user?: string | null,
      password?: string | null
    ) {
      this._url = String(url);
      this._method = method.toUpperCase();
      return super.open(method, url, async, user ?? null, password ?? null);
    }

    send(body?: Document | XMLHttpRequestBodyInit | null) {
      if (this._url.includes(DYNAMICS_HOST) && this._method === "POST") {
        this.addEventListener("load", () => {
          if (this.status >= 200 && this.status < 300) {
            fireSuccess();
          }
        });
      }
      return super.send(body);
    }
  };
}

function attachFormListeners(form: HTMLFormElement): void {
  form.addEventListener("submit", () => {
    requestAnimationFrame(() => {
      const hasErrors =
        form.querySelectorAll(".marketingForm__error.has-error").length > 0;

      if (hasErrors) {
        fireError();
      }
    });
  });
}

function whenEtrackerReady(callback: () => void, maxWait = 10_000): void {
  const deadline = Date.now() + maxWait;

  const poll = () => {
    if (typeof window._etracker?.sendEvent === "function") {
      callback();
      return;
    }
    if (Date.now() < deadline) {
      requestAnimationFrame(poll);
    }
  };

  poll();
}

export function initFormTracking(mountSelector = "[data-form-id]"): void {
  patchFetch();
  patchXHR();

  const tryInit = (): boolean => {
    const form = document.querySelector<HTMLFormElement>(FORM_SELECTOR);
    if (!form) return false;

    whenEtrackerReady(fireLoaded);

    attachFormListeners(form);
    return true;
  };

  if (!tryInit()) {
    const root =
      document.querySelector<HTMLElement>(mountSelector) ?? document.body;

    const observer = new MutationObserver((_mutations, obs) => {
      if (tryInit()) {
        obs.disconnect();
      }
    });

    observer.observe(root, { childList: true, subtree: true });
  }
}
